import React, { useState } from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useGetStoreNameAndUrl from "@editor/hooks/useGetStoreNameAndUrl";
import { createTemplatePreviewProps } from "@editor/hooks/useSaveComponentTemplate";
import { useEditorStore } from "@editor/store";
import { trpcClient } from "@editor/utils/trpc";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { useForm } from "react-hook-form";
import { z } from "zod";

const extractFromUrlFormSchema = z.object({
  url: z.string().url(),
});

type ExtractFromUrlFormData = z.infer<typeof extractFromUrlFormSchema>;

const ExtractFromUrlForm: React.FC<{}> = () => {
  const projectId = useCurrentProjectId();
  const { storeUrl } = useGetStoreNameAndUrl();
  const store = useEditorStore();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExtractFromUrlFormData>({
    resolver: zodResolver(extractFromUrlFormSchema),
    defaultValues: {
      url: "",
    },
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSubmit(async ({ url }) => {
      setIsLoading(true);
      try {
        const previewRenderProps = createTemplatePreviewProps(
          store.getState(),
          storeUrl ?? "",
        );

        if (!previewRenderProps || !projectId) {
          toast({
            header: "Error",
            message: "Please save a component first",
          });
          return;
        }
        await trpcClient.ai.generateComponentTemplatesFromUrl.query({
          url,
          projectId,
          storeContext: "",
        });

        toast({
          header: "Success",
          message: "Components are being extracted",
        });
      } catch (error) {
        toast({
          header: "Error extracting from URL",
          message: "Please try again",
        });
        console.error("Error extracting from url", error);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <span className="header-h5">Enter URL</span>
      <Input placeholder="https://www.mywebsite.com" {...register("url")} />
      {errors.url && (
        <span className="header-h5 text-danger">{errors.url.message}</span>
      )}

      <Button type="submit" variant="primary" isLoading={isLoading}>
        Pull from URL
      </Button>
    </form>
  );
};

export default ExtractFromUrlForm;
